import { Trans, useLingui } from "@lingui/react/macro";

import { share } from "../../model/change";
import { DEVICE_LABELS } from "../../model/DEVICE_LABELS";
import type { DashboardDevices } from "../../model/TeamDashboard";
import { DashboardCard } from "./DashboardCard";

type DevicesProps = {
  devices: DashboardDevices;
};

// «Телефон и компьютер»: посетители Umami по типу устройства, новые игроки — с чего заходили
// в день первого входа, «Вошли» — доля посетителей. Ниже — сколько пришедших с телефона потом
// зашли с компьютера и с чего заходят активные за период.
export const Devices = ({ devices }: DevicesProps) => {
  const { i18n, t } = useLingui();
  const number = new Intl.NumberFormat(i18n.locale);
  const percent = new Intl.NumberFormat(i18n.locale, {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const moved = number.format(devices.mobile_then_desktop);
  const fromPhone = number.format(devices.mobile_first);
  const active = [
    { key: "mobile", label: t`phone only`, value: devices.active_mobile_only },
    { key: "desktop", label: t`computer only`, value: devices.active_desktop_only },
    { key: "both", label: t`both`, value: devices.active_both },
    ...(devices.active_unknown > 0
      ? [{ key: "unknown", label: t`unknown (before tracking)`, value: devices.active_unknown }]
      : []),
  ];

  return (
    <DashboardCard
      title={<Trans>Phone and computer</Trans>}
      description={<Trans>Visitors — Umami; new players — what they used on their first day</Trans>}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[360px] border-collapse text-[13px]">
          <thead>
            <tr className="text-left text-xs text-subtle">
              <th scope="col" className="pt-1 pr-2 pb-2 font-semibold">
                <Trans>Device</Trans>
              </th>
              <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                <Trans>Visitors</Trans>
              </th>
              <th scope="col" className="px-2 pt-1 pb-2 text-right font-semibold">
                <Trans>New players</Trans>
              </th>
              <th scope="col" className="pt-1 pb-2 pl-2 text-right font-semibold">
                <Trans>Signed in</Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {devices.rows.map((row) => {
              const conversion = share(row.new_players, row.visitors);
              return (
                <tr key={row.device} data-device={row.device} className="border-t border-border/60">
                  <th
                    scope="row"
                    className="py-1.5 pr-2 text-left font-medium text-foreground-soft"
                  >
                    {i18n._(DEVICE_LABELS[row.device])}
                  </th>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                    {row.visitors === null ? "—" : number.format(row.visitors)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-semibold tabular-nums">
                    {number.format(row.new_players)}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-mono text-muted tabular-nums">
                    {conversion === null ? "—" : percent.format(conversion)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3 border-t border-border/60 pt-3">
        <div className="flex flex-col">
          <span className="text-xs text-subtle">
            <Trans>Came from a phone, later used a computer</Trans>
          </span>
          <span className="font-mono text-lg font-semibold" data-value="mobile-then-desktop">
            {t`${moved} of ${fromPhone}`}
          </span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-subtle">
            <Trans>Active in the period</Trans>
          </span>
          {active.map((row) => (
            <span
              key={row.key}
              data-active-device={row.key}
              className="flex justify-between gap-2.5 text-[13px]"
            >
              <span className="text-foreground-soft">{row.label}</span>
              <span className="font-mono">{number.format(row.value)}</span>
            </span>
          ))}
        </div>
      </div>
    </DashboardCard>
  );
};
